import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAdminBonusZone,
  deactivateAdminBonusZone,
  fetchAdminBonusExpansions,
  fetchAdminBonusZones,
  setAdminBonusExpansionEnabled,
  setAdminBonusZoneEnabled,
  updateAdminBonusZone,
  type AdminBonusExpansion,
  type AdminBonusZone,
  type AdminBonusZonePayload
} from '../api/adminBonusZones';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { getActiveServerSlug } from '../utils/siteContext';

const EMPTY_FORM = {
  id: '',
  name: '',
  slug: '',
  expansionId: '',
  minLevel: '',
  maxLevel: '',
  zoneType: '',
  legacyZoneId: '',
  isActive: true,
  isEnabled: true
};

type ZoneFormState = typeof EMPTY_FORM;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function numberOrNull(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function zoneToForm(zone: AdminBonusZone): ZoneFormState {
  return {
    id: zone.id,
    name: zone.name,
    slug: zone.slug,
    expansionId: zone.expansionId,
    minLevel: zone.minLevel == null ? '' : String(zone.minLevel),
    maxLevel: zone.maxLevel == null ? '' : String(zone.maxLevel),
    zoneType: zone.zoneType ?? '',
    legacyZoneId: zone.legacyZoneId == null ? '' : String(zone.legacyZoneId),
    isActive: zone.isActive,
    isEnabled: !zone.disabledByServerOverride
  };
}

function formToPayload(form: ZoneFormState, serverSlug: string): AdminBonusZonePayload {
  return {
    serverSlug,
    name: form.name.trim(),
    slug: form.slug.trim() || slugify(form.name),
    expansionId: form.expansionId,
    minLevel: numberOrNull(form.minLevel),
    maxLevel: numberOrNull(form.maxLevel),
    zoneType: form.zoneType.trim() || null,
    legacyZoneId: numberOrNull(form.legacyZoneId),
    isActive: form.isActive,
    isEnabled: form.isEnabled
  };
}

function zoneMeta(zone: AdminBonusZone) {
  const levelRange = zone.minLevel == null && zone.maxLevel == null
    ? null
    : zone.minLevel != null && zone.maxLevel != null
      ? `Lv ${zone.minLevel}-${zone.maxLevel}`
      : zone.minLevel != null
        ? `Lv ${zone.minLevel}+`
        : `Up to ${zone.maxLevel}`;

  return [zone.expansion, levelRange, zone.zoneType].filter(Boolean).join(' · ');
}

function serverNameForSlug(serverSlug: string) {
  return serverSlug.charAt(0).toUpperCase() + serverSlug.slice(1);
}

function ExpansionToggleCard({ expansions, serverName, pending, onToggle }: {
  expansions: AdminBonusExpansion[];
  serverName: string;
  pending: boolean;
  onToggle: (expansion: AdminBonusExpansion) => void;
}) {
  return (
    <div className="card shadow-sm h-100">
      <div className="card-header fw-semibold">Expansion availability</div>
      <div className="card-body">
        <p className="small text-body-secondary mb-3">
          Enabled expansions make all active zones in that expansion available on {serverName}, unless a zone has an explicit server disable.
        </p>
        <div className="list-group list-group-flush">
          {expansions.map((expansion) => (
            <label className="list-group-item list-group-item-action d-flex justify-content-between align-items-center px-0" key={expansion.id}>
              <span>
                <span className="fw-semibold">{expansion.name}</span>
                <span className="d-block small text-body-secondary">/{expansion.slug}</span>
              </span>
              <input
                className="form-check-input ms-3"
                type="checkbox"
                checked={expansion.enabledForServer}
                disabled={pending}
                onChange={() => onToggle(expansion)}
              />
            </label>
          ))}
          {expansions.length === 0 ? <div className="text-body-secondary small">No active expansions are available.</div> : null}
        </div>
      </div>
    </div>
  );
}

export function AdminBonusZonesPage() {
  const queryClient = useQueryClient();
  const serverSlug = getActiveServerSlug();
  const serverName = serverNameForSlug(serverSlug);
  const [form, setForm] = useState<ZoneFormState>(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const zonesQuery = useQuery({
    queryKey: ['admin', 'bonus-zones', serverSlug],
    queryFn: () => fetchAdminBonusZones(serverSlug)
  });

  const expansionsQuery = useQuery({
    queryKey: ['admin', 'bonus-expansions', serverSlug],
    queryFn: () => fetchAdminBonusExpansions(serverSlug)
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'bonus-zones'] });
    await queryClient.invalidateQueries({ queryKey: ['admin', 'bonus-expansions'] });
    await queryClient.invalidateQueries({ queryKey: ['bonus-zones'] });
    await queryClient.invalidateQueries({ queryKey: ['bonus-zone-trends'] });
    await queryClient.invalidateQueries({ queryKey: ['bonuses'] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: AdminBonusZonePayload) => createAdminBonusZone(payload),
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      await invalidate();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AdminBonusZonePayload }) => updateAdminBonusZone(id, payload),
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      await invalidate();
    }
  });

  const enabledMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) => setAdminBonusZoneEnabled(id, serverSlug, isEnabled),
    onSuccess: invalidate
  });

  const expansionMutation = useMutation({
    mutationFn: ({ expansionId, isEnabled }: { expansionId: string; isEnabled: boolean }) => setAdminBonusExpansionEnabled(expansionId, serverSlug, isEnabled),
    onSuccess: invalidate
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deactivateAdminBonusZone(id),
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      await invalidate();
    }
  });

  const zones = zonesQuery.data?.zones ?? [];
  const expansions = expansionsQuery.data?.expansions ?? [];
  const filteredZones = useMemo(() => {
    const term = search.trim().toLowerCase();
    return zones.filter((zone) => {
      if (!showInactive && !zone.isActive) {
        return false;
      }

      if (!term) {
        return true;
      }

      return [zone.name, zone.slug, zone.expansion, zone.zoneType, String(zone.legacyZoneId ?? '')]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [zones, search, showInactive]);

  const pending = createMutation.isPending || updateMutation.isPending || enabledMutation.isPending || expansionMutation.isPending || deactivateMutation.isPending;
  const error = createMutation.error || updateMutation.error || enabledMutation.error || expansionMutation.error || deactivateMutation.error;

  function updateField<K extends keyof ZoneFormState>(key: K, value: ZoneFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleNameChange(value: string) {
    setForm((current) => ({
      ...current,
      name: value,
      slug: current.id || current.slug ? current.slug : slugify(value)
    }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const payload = formToPayload(form, serverSlug);
    if (form.id) {
      updateMutation.mutate({ id: form.id, payload });
      return;
    }

    createMutation.mutate(payload);
  }

  function resetForm() {
    setForm(EMPTY_FORM);
  }

  return (
    <div className="container-fluid px-3 px-lg-4 py-4">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h2 mb-1">Bonus Zone Management</h1>
          <p className="text-body-secondary mb-0">Manage Resource Hunter expansion releases and zone exceptions for {serverName}.</p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => { void zonesQuery.refetch(); void expansionsQuery.refetch(); }} disabled={zonesQuery.isFetching || expansionsQuery.isFetching}>
          <i className="bi bi-arrow-clockwise me-1" /> Refresh
        </button>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-xl-4">
          <ExpansionToggleCard
            expansions={expansions}
            serverName={serverName}
            pending={pending}
            onToggle={(expansion) => expansionMutation.mutate({ expansionId: expansion.id, isEnabled: !expansion.enabledForServer })}
          />
        </div>
        <div className="col-xl-4">
          <form className="card shadow-sm h-100" onSubmit={handleSubmit}>
            <div className="card-header fw-semibold">{form.id ? 'Edit zone' : 'Add zone'}</div>
            <div className="card-body d-flex flex-column gap-3">
              <div>
                <label className="form-label" htmlFor="bonusZoneName">Name</label>
                <input id="bonusZoneName" className="form-control" value={form.name} onChange={(event) => handleNameChange(event.target.value)} required />
              </div>
              <div>
                <label className="form-label" htmlFor="bonusZoneSlug">Slug</label>
                <input id="bonusZoneSlug" className="form-control" value={form.slug} onChange={(event) => updateField('slug', slugify(event.target.value))} placeholder="auto-generated-from-name" />
              </div>
              <div>
                <label className="form-label" htmlFor="bonusZoneExpansion">Expansion</label>
                <select id="bonusZoneExpansion" className="form-select" value={form.expansionId} onChange={(event) => updateField('expansionId', event.target.value)} required>
                  <option value="">Select expansion</option>
                  {expansions.map((expansion) => <option value={expansion.id} key={expansion.id}>{expansion.name}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" htmlFor="bonusZoneType">Indoor / Outdoor</label>
                <select id="bonusZoneType" className="form-select" value={form.zoneType} onChange={(event) => updateField('zoneType', event.target.value)}>
                  <option value="">Unknown</option>
                  <option value="Indoor">Indoor</option>
                  <option value="Outdoor">Outdoor</option>
                </select>
              </div>
              <div className="row g-3">
                <div className="col-sm-4">
                  <label className="form-label" htmlFor="bonusZoneMinLevel">Min level</label>
                  <input id="bonusZoneMinLevel" className="form-control" type="number" min={1} value={form.minLevel} onChange={(event) => updateField('minLevel', event.target.value)} />
                </div>
                <div className="col-sm-4">
                  <label className="form-label" htmlFor="bonusZoneMaxLevel">Max level</label>
                  <input id="bonusZoneMaxLevel" className="form-control" type="number" min={1} value={form.maxLevel} onChange={(event) => updateField('maxLevel', event.target.value)} />
                </div>
                <div className="col-sm-4">
                  <label className="form-label" htmlFor="bonusZoneLegacyId">Legacy ID</label>
                  <input id="bonusZoneLegacyId" className="form-control" type="number" value={form.legacyZoneId} onChange={(event) => updateField('legacyZoneId', event.target.value)} />
                </div>
              </div>
              <div className="d-flex flex-wrap gap-4">
                <div className="form-check form-switch">
                  <input id="bonusZoneIsActive" className="form-check-input" type="checkbox" checked={form.isActive} onChange={(event) => updateField('isActive', event.target.checked)} />
                  <label className="form-check-label" htmlFor="bonusZoneIsActive">Catalog active</label>
                </div>
                <div className="form-check form-switch">
                  <input id="bonusZoneIsEnabled" className="form-check-input" type="checkbox" checked={form.isEnabled} onChange={(event) => updateField('isEnabled', event.target.checked)} />
                  <label className="form-check-label" htmlFor="bonusZoneIsEnabled">Enabled on {serverName}</label>
                </div>
              </div>
              {error ? <div className="alert alert-danger mb-0">{error.message}</div> : null}
            </div>
            <div className="card-footer d-flex flex-wrap gap-2 justify-content-end">
              {form.id ? <button className="btn btn-outline-secondary" type="button" onClick={resetForm}>Cancel edit</button> : null}
              <button className="btn btn-primary" type="submit" disabled={pending}>{form.id ? 'Save zone' : 'Add zone'}</button>
            </div>
          </form>
        </div>
        <div className="col-xl-4">
          <div className="card shadow-sm h-100">
            <div className="card-header fw-semibold">Availability rules</div>
            <div className="card-body small text-body-secondary d-flex flex-column gap-2">
              <div>Expansion releases are authoritative. Zones are available only when their expansion is enabled for this server.</div>
              <div>Use the zone toggle only for exceptions, such as revamped or retired zones that should no longer appear for new bonuses.</div>
              <div>Deactivated catalog zones remain stored for historical data, but are hidden from new Resource Hunter submissions.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="fw-semibold">Zones</div>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="form-check form-switch mb-0">
              <input id="showInactiveBonusZones" className="form-check-input" type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
              <label className="form-check-label small" htmlFor="showInactiveBonusZones">Show inactive</label>
            </div>
            <input className="form-control form-control-sm" style={{ width: '16rem' }} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search zones" aria-label="Search zones" />
          </div>
        </div>
        {zonesQuery.isLoading || expansionsQuery.isLoading ? <div className="card-body"><LoadingState label="Loading zones" /></div> : null}
        {zonesQuery.isError ? <div className="card-body"><ErrorState title="Could not load zones" error={zonesQuery.error} /></div> : null}
        {expansionsQuery.isError ? <div className="card-body"><ErrorState title="Could not load expansions" error={expansionsQuery.error} /></div> : null}
        {zonesQuery.data && expansionsQuery.data ? (
          <div className="table-responsive">
            <table className="table table-dark table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Zone</th>
                  <th>Metadata</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredZones.map((zone) => (
                  <tr key={zone.id}>
                    <td>
                      <div className="fw-semibold">{zone.name}</div>
                      <div className="small text-body-secondary">/{zone.slug}</div>
                    </td>
                    <td>{zoneMeta(zone) || <span className="text-body-secondary">No metadata</span>}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <span className={`badge ${zone.isActive ? 'text-bg-success' : 'text-bg-secondary'}`}>{zone.isActive ? 'Catalog active' : 'Catalog inactive'}</span>
                        <span className={`badge ${zone.expansionEnabledForServer ? 'text-bg-info' : 'text-bg-secondary'}`}>{zone.expansionEnabledForServer ? 'Expansion enabled' : 'Expansion disabled'}</span>
                        <span className={`badge ${zone.enabledForServer ? 'text-bg-primary' : 'text-bg-secondary'}`}>{zone.enabledForServer ? `Available on ${serverName}` : `Unavailable on ${serverName}`}</span>
                        {zone.disabledByServerOverride ? <span className="badge text-bg-warning text-dark">Zone disabled override</span> : null}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2 justify-content-end">
                        <button className="btn btn-outline-light btn-sm" type="button" onClick={() => setForm(zoneToForm(zone))}>Edit</button>
                        <button className="btn btn-outline-info btn-sm" type="button" onClick={() => enabledMutation.mutate({ id: zone.id, isEnabled: zone.disabledByServerOverride })} disabled={pending || !zone.isActive}>
                          {zone.disabledByServerOverride ? 'Enable zone' : 'Disable zone'}
                        </button>
                        {zone.isActive ? (
                          <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => deactivateMutation.mutate(zone.id)} disabled={pending}>
                            Deactivate
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredZones.length === 0 ? (
                  <tr><td colSpan={4} className="text-body-secondary">No zones match the current filters.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
